import { ScrollToTopButton } from '@wix/package-picker-components';
import { withExperiments } from '@wix/wix-experiments-react';
import classNames from 'classnames';
import { ActionButton } from 'components/SunriseApp/ActionButton';
import { CardsContainer } from '../CardsContainer/CardsContainer';
import { observer } from 'mobx-react';
import { withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { useProductsGridRefContext } from 'providers/ProductsGridContextRefProvider/ProductsGridContextRefProvider';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@wix/design-system';
import { PlansPageFooter } from '../../../pages/PlansPage/Footer/PlansPageFooter';
import { LimitedTimeBanner } from '../../../pages/PlansPage/LimitedTimeBanner/LimitedTimeBanner';
import { NoTabsBanner } from '../../../pages/PlansPage/PaymentBanners';
import { usePlansPageController } from '../../../pages/PlansPage/PlansPage.controller';
import { PlansPageProps } from '../../../pages/PlansPage/PlansPageProps';
import { useGetProducts } from '../../../pages/PlansPage/shared/useGetProducts';
import { withGtmLogger } from '../../HOCs/withGtmLogger';
import { AllPlansInclude } from '../AllPlansInclude/AllPlansInclude';
import { PlansGrid } from '../PlansGrid/PlansGrid';
import { useStudioProductCards } from '../hooks/useStudioProductCards';
import { useScrollToTopButtonVisibility } from '../hooks/useScrollToTopButtonVisibility';
import s from './PlansPage.scss';
import { Z_INDEX } from '../constants/zIndexes';

const PlansPageComp: React.FC<PlansPageProps> = (props) => {
  const { appStore } = props.rootStore;
  const {
    appContext: { featureGroups, biLogger },
    shouldStoreIntentNoTabs,
    jumpToTab,
    scrollToTop,
  } = appStore;
  const { t } = useTranslation();
  const { shouldShowLimitedTimeBanner, onSelectProduct } =
    usePlansPageController(props);
  const { scrollToRef } = useProductsGridRefContext();
  const { products } = useGetProducts({
    appStore,
  });
  const { theme } = usePageModeContext();
  const productsCardsData = useStudioProductCards({
    baseProducts: products,
    appStore,
    clickSource: 'cards',
    onSelectProduct: (product) => onSelectProduct(product, 'cards'),
  });
  const onComparePlanFeaturesClick = () => {
    biLogger.logComparePlanFeaturesCTA({});
    scrollToRef();
  };

  const cardsContainerRef = React.useRef(null);
  const { shouldShowScrollToTopButton } =
    useScrollToTopButtonVisibility(cardsContainerRef);

  return (
    <div
      data-testid='advanced-plans-page'
      className={classNames(props.className, s.root, theme)}
    >
      <CardsContainer productsCardsData={productsCardsData} />
      <div ref={cardsContainerRef} />
      {/* TODO build wix studio cards container*/}
      {shouldShowLimitedTimeBanner && <LimitedTimeBanner />}
      {shouldStoreIntentNoTabs && <NoTabsBanner action={jumpToTab} />}
      <Box margin='SP3 0 SP6 0'>
        <ActionButton onClick={onComparePlanFeaturesClick}>
          {t('premium-purchase-plan.cards.compare_features.cta')}
        </ActionButton>
      </Box>
      <AllPlansInclude />
      <Box marginTop='SP6'>
        <PlansPageFooter />
      </Box>
      <Box marginTop='SP4' width='100%'>
        <PlansGrid
          onClickSelect={(product) => onSelectProduct(product, 'table')}
          features={[]}
          products={products}
          groups={featureGroups ?? []}
        />
      </Box>
      <Box
        position='fixed'
        right='20px'
        bottom='50px'
        zIndex={Z_INDEX.SCROLL_TO_TOP}
      >
        <ScrollToTopButton
          text={t('studio_package_picker.table.go_to_top')}
          onClick={scrollToTop}
          isVisible={shouldShowScrollToTopButton}
        ></ScrollToTopButton>
      </Box>
    </div>
  );
};

export const PlansPage = withGtmLogger(
  withExperiments(withInjectedRootStore(observer(PlansPageComp))),
);
